import React, { useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { debounce } from 'lodash';
import { Tag } from '@zydalabs/zac-react';
import cx from 'classnames';

import { context as userContext } from 'context/user';
import { context as localContext } from 'context/locale';
import { context as notificationsContext } from 'context/notifications';
import { context as customersContext } from 'context/customers';
import { ReactComponent as IconsInfo } from 'assets/blue-info.svg';
import { Field, Text } from 'components/service';
import { Stepper } from 'components/form/elements';
import { DropDown } from 'components/kit';
import { Label } from 'components/form/generic';
import { SEGMENT_TITLE_MAP } from 'constants/customers';
import SEGMENTS from 'common/schemas/segmentSchema';
import fuseSegmentContractWithSegmentStatic from 'common/utils/fuseSegmentContractWithSegmentStatic';
import {
  INACTVITY_PERIOD,
  INACTVITY_PERIOD_DESC,
  DAYS,
  EX_30,
  LOOK_BACK_WINDOW,
  LOOK_BACK_WINDOW_DESC,
  ALL_CUSTOMERS,
  LAST_YEAR,
  LAST_6_MONTHS,
  LAST_3_MONTHS,
  LAST_MONTH,
  MESSAGES_LIMIT,
  MESSAGES_LIMIT_DESC,
  INPUT_LABEL_SMS,
  EX_1000,
  NOTED_COOLDOWN_CAMPAIGN,
  SOMETHING_WENT_WRONG,
  MESSAGES_LIMIT_WARNING,
  SEGMENTS_TITLE,
  CONTACTS,
  USEFUL_TIPS,
  ADD_CONTACTS_RIGHT_WAY,
  CONTACTS_DOWNLOAD_INFO,
  CONTACTS_DOWNLOAD_BTN,
  NEW,
} from 'constants/translations';
import { CAMPAIGN_OBJECT_KEYS, LOOKBACK_WINDOW_KEYS, CAMPAIGN_TYPES, TAB_NAMES } from './constants';
import { ESTIMATE_REACH } from './schemas';
import TargetedSegmentRecipient from './TargetedSegmentRecipient';
import CampaignDetailsSection from './CampaignDetailsSection';
import CampaignContacts from './CampaignContacts';
import { fileControllerUtils } from './CampaignContacts/utils';

const CampaignAudienceStep = ({
  setFieldValue,
  handleChange,
  type,
  lookbackWindow,
  inactivityPeriod,
  estimateReach,
  targetedSegment,
  creditsPerMessage,
  targetedSegmentError,
  smsSendingLimit,
  estimateReachWithSendingLimit,
  averageOrderValue,
  totalOrders,
}) => {
  const { selectedStore } = useContext(userContext);
  const { lang, translate } = useContext(localContext);
  const notifications = useContext(notificationsContext);
  const {
    segments: contextSegments,
    selectedSegment,
    setSegments,
    isWithinSegmentJobIntervals,
    setSegmentRetrievalDate,
    audienceType,
    setAudienceType,
    setFileController,
  } = useContext(customersContext);

  const { loading: segmentLoading } = useQuery(SEGMENTS, {
    variables: {
      storeId: selectedStore.id,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: type === CAMPAIGN_TYPES.RETENTION || isWithinSegmentJobIntervals(),
    onCompleted: result => {
      const fusedSegments = {
        ...fuseSegmentContractWithSegmentStatic(
          result.segments.segments,
          result.segments.totalCustomerCount,
          contextSegments,
        ),
      };
      setSegments(fusedSegments);
      setSegmentRetrievalDate(new Date());
      const targetedSegmentId = targetedSegment || SEGMENT_TITLE_MAP.ALL_CUSTOMERS;
      setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH, fusedSegments[targetedSegmentId]?.customersCount);
    },
  });

  const [getEstimateReach, { loading }] = useMutation(ESTIMATE_REACH, {
    variables: {
      storeId: selectedStore.id,
    },
    onError: () => {
      notifications.show(<Text value={SOMETHING_WENT_WRONG} />, 'error');
    },
  });

  const setEstimateReachWithSendingLimit = (estimateReachResponse, smsSendingLimitValue) => {
    if (estimateReachResponse) {
      let estimate = '--';
      if (smsSendingLimitValue)
        estimate = estimateReachResponse > smsSendingLimitValue ? smsSendingLimitValue : estimateReachResponse;
      else estimate = estimateReachResponse;
      setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH_WITH_SENDING_LIMIT, estimate);
    }
  };

  const handleGetEstimateReach = async (typeParam, inactivityPeriodParam, lookbackWindowParam) => {
    try {
      const response = await getEstimateReach({
        variables: {
          type: typeParam,
          inactivityPeriod: inactivityPeriodParam,
          lookbackWindow: lookbackWindowParam,
        },
      });
      const estimateReachResponse = response?.data?.estimateReach?.estimateReach;
      if (estimateReachResponse >= 0) {
        setEstimateReachWithSendingLimit(estimateReachResponse, smsSendingLimit);
        setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH, estimateReachResponse);
      }
    } catch {
      notifications.show(<Text value={SOMETHING_WENT_WRONG} />, 'error');
    }
  };

  const debouncedHandleGetEstimateReach = useMemo(
    () =>
      debounce(
        (typeParam, inactivityPeriodParam, lookbackWindowParam) =>
          handleGetEstimateReach(typeParam, inactivityPeriodParam, lookbackWindowParam),
        650,
      ),
    [type, lookbackWindow, smsSendingLimit],
  );

  const handleSetTargetedSegment = segment => {
    setFieldValue(CAMPAIGN_OBJECT_KEYS.TARGETED_SEGMENT, segment);
    setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH, contextSegments[segment]?.customersCount);
    setFieldValue(CAMPAIGN_OBJECT_KEYS.CAMPAIGN_GOAL, 'No goal');
    if (creditsPerMessage)
      setFieldValue(
        CAMPAIGN_OBJECT_KEYS.ESTIMATED_CREDITS,
        contextSegments[segment]?.customersCount * creditsPerMessage,
      );
  };

  const handlePhoneUploadTabClick = () => {
    setFieldValue(CAMPAIGN_OBJECT_KEYS.TARGETED_SEGMENT, undefined);
    setFieldValue(CAMPAIGN_OBJECT_KEYS.USE_CONTACT_IMPORT, true);
    setAudienceType(TAB_NAMES.CONTACTS);
  };
  useEffect(() => {
    if (!Number.isNaN(targetedSegment) && type === CAMPAIGN_TYPES.SEGMENT_TARGETING && isWithinSegmentJobIntervals()) {
      setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH, contextSegments[targetedSegment]?.customersCount);
      if (creditsPerMessage)
        setFieldValue(
          CAMPAIGN_OBJECT_KEYS.ESTIMATED_CREDITS,
          contextSegments[targetedSegment]?.customersCount * creditsPerMessage,
        );
    } else if (selectedSegment) {
      setFieldValue(CAMPAIGN_OBJECT_KEYS.ESTIMATE_REACH, contextSegments[selectedSegment]?.customersCount);
      if (creditsPerMessage)
        setFieldValue(
          CAMPAIGN_OBJECT_KEYS.ESTIMATED_CREDITS,
          contextSegments[targetedSegment]?.customersCount * creditsPerMessage,
        );
    }

    // set upload phone numbers to be open by default
    if (type === CAMPAIGN_TYPES.ANNOUNCEMENT) {
      handlePhoneUploadTabClick();
    }
  }, []);

  return type === CAMPAIGN_TYPES.RETENTION ? (
    <div className="flex flex-col-reverse md:flex-row pt-4 w-full pb-40">
      <div className={cx('w-full mt-6 md:w-8/12 md:mt-0', lang === 'en' ? 'md:pr-4' : 'md:pl-4')}>
        <div className="w-full mb-6">
          <Label title={<Text value={LOOK_BACK_WINDOW} />} subtitle={<Text value={LOOK_BACK_WINDOW_DESC} />}>
            <div style={{ direction: 'ltr' }}>
              <DropDown
                float="left"
                optionSelected={lookbackWindow}
                onSelect={option => {
                  setFieldValue(CAMPAIGN_OBJECT_KEYS.LOOKBACK_WINDOW, option);
                  option !== lookbackWindow && handleGetEstimateReach(type, parseInt(inactivityPeriod) || 0, option);
                }}
                data={[
                  {
                    id: LOOKBACK_WINDOW_KEYS.ALL_CUSTOMERS,
                    title: <Text value={ALL_CUSTOMERS} />,
                  },
                  {
                    id: LOOKBACK_WINDOW_KEYS.LAST_YEAR,
                    title: <Text value={LAST_YEAR} />,
                  },
                  {
                    id: LOOKBACK_WINDOW_KEYS.LAST_6_MONTHS,
                    title: <Text value={LAST_6_MONTHS} />,
                  },
                  {
                    id: LOOKBACK_WINDOW_KEYS.LAST_3_MONTHS,
                    title: <Text value={LAST_3_MONTHS} />,
                  },
                  {
                    id: LOOKBACK_WINDOW_KEYS.LAST_MONTH,
                    title: <Text value={LAST_MONTH} />,
                  },
                ]}
                icon="keyboard_arrow_down"
                position={lang === 'ar' ? 'right' : 'left'}
                width="w-40"
                wFull
              />
            </div>
          </Label>
        </div>
        <div className="w-full mb-6">
          <Label
            title={<Text value={INACTVITY_PERIOD} />}
            subtitle={<Text className="text-gray-600" value={INACTVITY_PERIOD_DESC} />}
          >
            <Field
              name={CAMPAIGN_OBJECT_KEYS.INACTIVITY_PERIOD}
              type="number"
              left={<Text value={DAYS} className="capitalize" />}
              min={0}
              max={999999999}
              step={1}
              placeholder={translate(EX_30)}
              onChange={e => {
                handleChange(e);
                if (e.target.value) debouncedHandleGetEstimateReach(type, parseInt(e.target.value), lookbackWindow);
              }}
              component={Stepper}
              width="64"
            />
          </Label>
        </div>
        <div className="w-full mb-12">
          <Label
            title={<Text value={MESSAGES_LIMIT} />}
            subtitle={
              <>
                <Text className="text-gray-600" value={MESSAGES_LIMIT_DESC} />
                <Text className="text-gray-600 font-semibold" value={MESSAGES_LIMIT_WARNING} />
              </>
            }
          >
            <Field
              name={CAMPAIGN_OBJECT_KEYS.SMS_SENDING_LIMIT}
              type="number"
              left={<Text value={INPUT_LABEL_SMS} className="whitespace-nowrap" />}
              min={0}
              step={1}
              placeholder={translate(EX_1000)}
              component={Stepper}
              width="64"
              onChange={e => {
                handleChange(e);
                setEstimateReachWithSendingLimit(estimateReach, e.target.value);
              }}
            />
          </Label>
        </div>
        <div className="flex items-center text-gray-600 p-4 border border-blue-600 bg-primary-50">
          <div className={cx('pb-4', lang === 'en' ? 'pr-3' : 'pl-3')}>
            <IconsInfo className="inline" />
          </div>
          <Text value={NOTED_COOLDOWN_CAMPAIGN} className="inline" />
        </div>
      </div>
      <div className="justify-end whitespace-no-wrap w-full sticky top-0 bg-white pt-4 md:pt-0 md:w-4/12 md:relative">
        <CampaignDetailsSection
          loading={loading}
          estimateReach={estimateReachWithSendingLimit}
          lang={lang}
          averageOrderValue={averageOrderValue}
          totalOrders={totalOrders}
        />
      </div>
    </div>
  ) : (
    <div className="pb-32 md:pb-40 lg:pb-32 mt-10">
      <div className="flex flex-col-reverse md:flex-row">
        <div className={cx('w-full', audienceType === TAB_NAMES.CONTACTS && 'md:w-8/12')}>
          <div className="bg-gray-200 flex">
            {type === CAMPAIGN_TYPES.ANNOUNCEMENT && (
              <div
                className={cx(
                  'p-4 w-1/2 flex items-center',
                  audienceType === TAB_NAMES.CONTACTS && 'bg-gray-100 border-b border-primary-base',
                )}
                aria-hidden
                onClick={() => handlePhoneUploadTabClick()}
              >
                <Text className={cx('w-auto', lang === 'ar' ? 'ml-2' : 'mr-2')} value={CONTACTS} />
                <Tag
                  color="positive"
                  inner={{
                    text: translate(NEW),
                  }}
                  size="small"
                />
              </div>
            )}
            <div
              className={cx(
                'p-4 flex',
                audienceType === TAB_NAMES.SEGMENTS && 'bg-gray-100 border-b border-primary-base',
                type === CAMPAIGN_TYPES.SEGMENT_TARGETING ? 'w-full' : 'w-1/2',
              )}
              aria-hidden
              onClick={() => {
                setFieldValue(CAMPAIGN_OBJECT_KEYS.USE_CONTACT_IMPORT, false);
                setFileController(fileControllerUtils);
                setAudienceType(TAB_NAMES.SEGMENTS);
              }}
            >
              <Text className="text-primary-base" value={SEGMENTS_TITLE} />
            </div>
          </div>
          <div className="p-4 pt-6 bg-gray-100">
            {audienceType === TAB_NAMES.SEGMENTS && (
              <TargetedSegmentRecipient
                setTargetedSegment={handleSetTargetedSegment}
                currentSelectedSegment={targetedSegment}
                loading={segmentLoading}
                targetedSegmentError={targetedSegmentError}
              />
            )}

            {audienceType === TAB_NAMES.CONTACTS && <CampaignContacts setFieldValue={setFieldValue} />}
          </div>
        </div>
        {audienceType === TAB_NAMES.CONTACTS && (
          <div className="justify-end w-full sticky top-0 bg-white pb-4 md:pt-0 md:w-4/12 md:relative">
            <div className="md:px-6">
              <Text value={USEFUL_TIPS} className="mb-4 font-semibold" />
              <div className="bg-gray-100 p-4">
                <Text value={ADD_CONTACTS_RIGHT_WAY} className="mb-2" />
                <Text value={CONTACTS_DOWNLOAD_INFO} className="mb-4 text-xs text-zyda-grey-70" />

                <div
                  className="w-full"
                  onClick={() => (window.location.href = `${process.env.PUBLIC_URL}/phone_numbers_template.xlsx`)}
                  aria-hidden="true"
                >
                  <Text className="text-xs cursor-pointer text-primary-base" value={CONTACTS_DOWNLOAD_BTN} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CampaignAudienceStep.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  type: PropTypes.oneOf([CAMPAIGN_TYPES.RETENTION, CAMPAIGN_TYPES.SEGMENT_TARGETING]),
  lookbackWindow: PropTypes.oneOf([
    LOOKBACK_WINDOW_KEYS.LAST_MONTH,
    LOOKBACK_WINDOW_KEYS.LAST_YEAR,
    LOOKBACK_WINDOW_KEYS.ALL_CUSTOMERS,
    LOOKBACK_WINDOW_KEYS.LAST_3_MONTHS,
    LOOKBACK_WINDOW_KEYS.LAST_6_MONTHS,
  ]).isRequired,
  inactivityPeriod: PropTypes.number,
  estimateReach: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  targetedSegment: PropTypes.oneOf([
    SEGMENT_TITLE_MAP.ALL_CUSTOMERS,
    SEGMENT_TITLE_MAP.CHAMPIONS,
    SEGMENT_TITLE_MAP.LOYAL,
    SEGMENT_TITLE_MAP.PROMISING,
    SEGMENT_TITLE_MAP.NEW,
    SEGMENT_TITLE_MAP.LOW_VALUE,
    SEGMENT_TITLE_MAP.NEEDS_ATTENTION,
  ]),
  creditsPerMessage: PropTypes.number,
  targetedSegmentError: PropTypes.shape({ props: PropTypes.shape({ value: PropTypes.arrayOf(PropTypes.string) }) }),
  smsSendingLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  estimateReachWithSendingLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  averageOrderValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalOrders: PropTypes.number,
};

export default CampaignAudienceStep;
